import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import hrApi from "./hrApi";
import SignaturePad from "./SignaturePad";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NameInputs } from "./NameInputs";
import { toast } from "sonner";
import { ShieldCheck, FileText, ExternalLink, CheckCircle2, Circle, Camera } from "lucide-react";
import { PdfPages, SignedCopies } from "./PackPdf";

const BASE = process.env.REACT_APP_BACKEND_URL || "";
function IdUpload({ token, f, value, onDone }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); setBusy(true);
    try { const r = await hrApi.post(`/public/onboarding/${token}/id-photo`, fd); onDone(r.data); toast.success("Photo uploaded"); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload failed — please try again"); }
    setBusy(false); e.target.value = "";
  };
  return (
    <div><label className="text-xs font-semibold text-slate-600">{f.label}{f.required ? " *" : ""}</label>
      <label className="mt-1 flex items-center gap-2 h-11 px-3 rounded-md border border-dashed border-slate-300 text-sm text-slate-600 cursor-pointer hover:bg-slate-50">
        {value ? <CheckCircle2 size={16} className="text-teal-600" /> : <Camera size={16} />}
        <span data-testid={`pack-f-${f.key}-status`} className="truncate">{busy ? "Uploading…" : value || "Take or choose a photo"}</span>
        <input data-testid={`pack-f-${f.key}`} type="file" accept="image/*,application/pdf" className="hidden" onChange={pick} disabled={busy} />
      </label></div>
  );
}
function Shell({ children }) { return <div className="min-h-screen bg-slate-100 py-6 px-2 sm:px-4"><div className="max-w-3xl mx-auto bg-white rounded-3xl shadow-sm p-4 sm:p-6">{children}</div></div>; }

export default function HrPack() {
  const { token } = useParams();
  const [pack, setPack] = useState(null);
  const [err, setErr] = useState("");
  const [vals, setVals] = useState({});
  const [name, setName] = useState("");
  const [ack, setAck] = useState(false);
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ticks, setTicks] = useState({});

  const loaded = React.useRef(-1);
  const load = () => hrApi.get(`/public/onboarding/${token}`).then((r) => {
    setPack(r.data);
    const i = r.data.steps.findIndex((s) => s.status !== "done");
    const cur = r.data.steps[i];
    if (cur && loaded.current !== i) { loaded.current = i; setVals(cur.saved || {}); setName((cur.saved && cur.saved.__name) || ""); }
  }).catch((e) => setErr(e.response?.data?.detail || "This link is not valid."));
  useEffect(() => { load(); }, [token]);

  if (err) return <Shell><div className="text-center text-slate-500" data-testid="pack-invalid">{err}</div></Shell>;
  if (!pack) return <Shell><div className="text-center text-slate-400">Loading…</div></Shell>;
  const steps = pack.steps || [];
  const idx = steps.findIndex((s) => s.status !== "done");
  const allDone = idx === -1 || pack.status === "completed";
  if (allDone) return <Shell><div className="text-center space-y-3" data-testid="pack-done">
    <div className="w-14 h-14 rounded-full bg-teal-100 grid place-items-center mx-auto"><ShieldCheck className="text-teal-600" size={28} /></div>
    <h1 className="font-heading font-extrabold text-2xl text-slate-800">{pack.single ? "Signed — thank you" : "Onboarding complete 🎉"}</h1>
    <p className="text-slate-500">Thanks{pack.employee_name ? `, ${pack.employee_name.split(" ")[0]}` : ""}! Everything has been filed with G &amp; L One.</p>
    <SignedCopies token={token} steps={steps} />
  </div></Shell>;

  const step = steps[idx];
  const missingRequired = step.type === "details" && (step.form_fields || []).some((f) => f.required && !String(vals[f.key] || "").trim());
  const setV = (k, v) => setVals((s) => ({ ...s, [k]: v }));
  const save = () => hrApi.post(`/public/onboarding/${token}/save`, { step_index: idx, field_values: { ...vals, __name: name } }).then(() => toast.success("Saved — resume anytime from this link")).catch(() => {});

  const submit = async () => {
    const payload = { step_index: idx, typed_name: name, acknowledged: ack, signature_data: sig, field_values: { ...vals, __tick_times: ticks } };
    if (step.type === "contract" && (!name.trim() || !ack)) return toast.error("Type your first and last name and tick to agree");
    if (step.type === "contract" && !sig) return toast.error("Please draw your signature");
    if (missingRequired) return toast.error("Please complete the required fields");
    if (step.type === "induction") {
      for (const f of step.form_fields || []) {
        if (f.type === "signature") continue;
        if (f.required && f.type === "checkbox" && !vals[f.key]) return toast.error(`Please tick: ${f.label}`);
        if (f.required && f.type !== "checkbox" && !vals[f.key]) return toast.error(`Please complete: ${f.label}`);
      }
      if (!sig) return toast.error("Please add your signature");
    }
    setBusy(true);
    try {
      await hrApi.post(`/public/onboarding/${token}/submit-step`, payload);
      setVals({}); setName(""); setAck(false); setSig(null); setTicks({});
      await load();
      toast.success("Step complete");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    setBusy(false);
  };

  return (
    <Shell>
      <div data-testid="pack-page" className="space-y-4">
        {!pack.single && <div className="flex items-center gap-2 text-xs font-semibold" data-testid="pack-progress">
          {steps.map((s, i) => (
            <div key={i} className={`flex items-center gap-1 ${i === idx ? "text-indigo-600" : s.status === "done" ? "text-teal-600" : "text-slate-400"}`}>
              {s.status === "done" ? <CheckCircle2 size={15} /> : <Circle size={15} />} {s.type === "contract" ? "Contract" : s.type === "induction" ? "Induction" : "Details"}
            </div>
          ))}
        </div>}
        <div><h1 className="font-heading font-extrabold text-2xl text-slate-800">{pack.single ? step.template_name : `Step ${idx + 1} of ${steps.length}: ${step.type === "contract" ? "Your contract" : step.type === "induction" ? step.template_name : "Your details"}`}</h1>
          {pack.employee_name && <p className="text-slate-500 text-sm">For {pack.employee_name}</p>}</div>

        {steps.some((s) => s.status === "done") && <SignedCopies token={token} steps={steps} />}
        {step.pdf && <PdfPages token={token} step={step} />}
        {step.body && <div className="rounded-xl border border-slate-200 p-4 bg-slate-50 text-sm text-slate-700 whitespace-pre-wrap max-h-80 overflow-y-auto" data-testid="pack-body">{step.body}</div>}

        {step.type === "induction" && (step.attachments || []).length > 0 && (
          <div className="space-y-1" data-testid="pack-attachments">
            <div className="text-xs font-semibold text-slate-600">Required reading — open each, then tick below</div>
            {step.attachments.map((a) => (
              <a key={a.id} data-testid={`pack-att-${a.id}`} href={`${BASE}/api/hr/public/onboarding/${token}/attachment/${a.id}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-indigo-600 hover:underline border border-slate-100 rounded-lg px-2 py-1.5"><FileText size={14} /> {a.title} <ExternalLink size={12} className="ml-auto" /></a>
            ))}
          </div>
        )}

        {(step.form_fields || []).length > 0 && (
          <div className="space-y-3" data-testid="pack-fields">
            {step.form_fields.map((f) => (
              <div key={f.key}>
                {f.type === "checkbox" ? (
                  <label className="flex items-start gap-2 text-sm text-slate-700"><input type="checkbox" data-testid={`pack-f-${f.key}`} checked={!!vals[f.key]} onChange={(e) => { setV(f.key, e.target.checked); setTicks((t) => ({ ...t, [f.key]: e.target.checked ? new Date().toISOString() : undefined })); }} className="w-4 h-4 mt-0.5 accent-indigo-500" /> {f.label}{f.required ? " *" : ""}</label>
                ) : f.type === "signature" ? (
                  <div><label className="text-xs font-semibold text-slate-600">{f.label}</label><SignaturePad onChange={setSig} /></div>
                ) : f.type === "file" ? (
                  <IdUpload token={token} f={f} value={vals[f.key]} onDone={(r) => setVals((s) => ({ ...s, [f.key]: `Uploaded: ${r.filename}`, [`${f.key}_doc`]: r.id }))} />
                ) : f.key === "full_name" ? (
                  <NameInputs key={`f${idx}`} value={vals[f.key] || ""} onChange={(v) => setV(f.key, v)} testid={`pack-f-${f.key}`} />
                ) : f.type === "select" ? (
                  <div><label className="text-xs font-semibold text-slate-600">{f.label}{f.required ? " *" : ""}</label>
                    <select data-testid={`pack-f-${f.key}`} value={vals[f.key] || ""} onChange={(e) => setV(f.key, e.target.value)} className="w-full h-11 mt-1 rounded-md border border-input bg-white px-3 text-sm">
                      <option value="">Select…</option>{(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select></div>
                ) : (
                  <div><label className="text-xs font-semibold text-slate-600">{f.label}{f.required ? " *" : ""}</label><Input data-testid={`pack-f-${f.key}`} type={f.type === "date" ? "date" : "text"} value={vals[f.key] || ""} onChange={(e) => setV(f.key, e.target.value)} className="h-11 mt-1" /></div>
                )}
              </div>
            ))}
          </div>
        )}

        {step.type === "contract" && (
          <div className="space-y-3">
            <div className="rounded-2xl border border-slate-200 p-3"><label className="text-xs font-semibold text-slate-600">Draw your signature *</label><SignaturePad onChange={setSig} /></div>
            <NameInputs key={`n${idx}`} value={name} onChange={setName} testid="pack-name" />
            <label className="flex items-start gap-2 text-sm text-slate-700"><input type="checkbox" data-testid="pack-ack" checked={ack} onChange={(e) => setAck(e.target.checked)} className="w-4 h-4 mt-0.5 accent-indigo-500" /> I have read and agree to this contract; this is my electronic signature.</label>
          </div>
        )}

        <div className="flex gap-2">
          <Button data-testid="pack-save" onClick={save} variant="outline" className="rounded-full h-12 flex-1">Save & resume later</Button>
          <Button data-testid="pack-submit" onClick={submit} disabled={busy} className="rounded-full h-12 flex-1 bg-indigo-500 hover:bg-indigo-400 text-white font-bold">{busy ? "Submitting…" : (idx + 1 < steps.length ? "Submit & continue" : "Finish")}</Button>
        </div>
        <p className="text-[11px] text-slate-400 text-center">Your name, time and IP are recorded for the audit trail.</p>
      </div>
    </Shell>
  );
}
